import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { TextField, Box, FormControl, InputLabel, Select, MenuItem, Button, CircularProgress, Link as MuiLink } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

// Custom centered loading overlay component
const CenteredLoadingOverlay = () => (
    <Box
        sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            zIndex: 1,
        }}
    >
        <CircularProgress size={60} />
    </Box>
);

const AllAttributesTable = ({ apiUrl }) => {
    const [players, setPlayers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [downloading, setDownloading] = useState(false);

    // Pagination state
    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 50,
    });
    const [rowCount, setRowCount] = useState(0);

    // State for filters
    const [selectedTeam, setSelectedTeam] = useState('');
    const [selectedPosition, setSelectedPosition] = useState('');
    const [selectedPlayerType, setSelectedPlayerType] = useState('');
    const [selectedSeason, setSelectedSeason] = useState('');

    // State for dropdown options
    const [teamOptions, setTeamOptions] = useState([]);
    const [positionOptions, setPositionOptions] = useState([]);
    const [seasonOptions, setSeasonOptions] = useState([]);

    // Ref to track if initial load is complete
    const initialLoadComplete = useRef(false);
    const filterOptionsLoaded = useRef(false);

    // Define this outside of dependencies to avoid unnecessary re-renders
    const baseballPositionOrder = useRef(['C', '1B', '2B', '3B', 'SS', 'LF', 'CF', 'RF', 'SP', 'RP']);

    // Build query parameters for API call
    const buildQueryParams = useCallback(() => {
        const params = new URLSearchParams();

        // Pagination
        params.append('page', paginationModel.page + 1);
        params.append('page_size', paginationModel.pageSize);

        // Search
        if (searchTerm) {
            params.append('search', searchTerm);
        }

        // Filters
        if (selectedTeam) params.append('team', selectedTeam);
        if (selectedPosition) params.append('position', selectedPosition);
        if (selectedPlayerType) params.append('player_type', selectedPlayerType);
        if (selectedSeason) params.append('season', selectedSeason);

        return params;
    }, [paginationModel, searchTerm, selectedTeam, selectedPosition, selectedPlayerType, selectedSeason]);

    // Fetch filter options once on mount
    const fetchFilterOptions = useCallback(async () => {
        if (filterOptionsLoaded.current) return;

        try {
            // Remove /paginated from the URL for filter-options endpoint
            const filterOptionsUrl = apiUrl.replace('/paginated', '/filter-options');
            const response = await fetch(filterOptionsUrl);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const filterOptions = await response.json();
            
            if (filterOptions.teams) {
                setTeamOptions(filterOptions.teams);
            }
            
            if (filterOptions.positions) {
                const positions = filterOptions.positions;
                positions.sort((a, b) => {
                    const indexA = baseballPositionOrder.current.indexOf(a);
                    const indexB = baseballPositionOrder.current.indexOf(b);
                    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                    if (indexA !== -1) return -1;
                    if (indexB !== -1) return 1;
                    return a.localeCompare(b);
                });
                setPositionOptions(positions);
            }
            
            if (filterOptions.seasons) {
                setSeasonOptions(filterOptions.seasons);
            }

            filterOptionsLoaded.current = true;
        } catch (error) {
            console.error("Error fetching filter options:", error);
        }
    }, [apiUrl]);

    // Fetch data with server-side pagination
    const fetchData = useCallback(async () => {
        setLoading(true);
        setPlayers([]); // Clear data immediately to show loading state

        try {
            const params = buildQueryParams();
            const url = `${apiUrl}?${params.toString()}`;

            const response = await fetch(url);
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const responseData = await response.json();

            // Handle paginated response
            const data = responseData.data || responseData;
            const paginationInfo = responseData.pagination || {};

            const playersWithIds = data.map((player, index) => ({
                ...player,
                id: player.uid || index,
                usernameDisplay: player.user ? player.user.username : 'N/A',
                user_url: player.user?.url || null,
                team_name: player.team ? player.team.team_name : 'N/A',
            }));

            setPlayers(playersWithIds);
            setRowCount(paginationInfo.total_count || data.length);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching data:", error);
            setError(error);
            setLoading(false);
        }
    }, [apiUrl, buildQueryParams]);

    // Load filter options once on mount
    useEffect(() => {
        fetchFilterOptions();
    }, [fetchFilterOptions]);

    // Initial load and refetch when dependencies change with debouncing
    useEffect(() => {
        if (!initialLoadComplete.current) {
            fetchData();
            initialLoadComplete.current = true;
            return;
        }

        // Debounce for search and filter changes (500ms)
        // Pagination changes are immediate
        const timeoutId = setTimeout(() => {
            fetchData();
        }, 500);

        return () => clearTimeout(timeoutId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginationModel, searchTerm, selectedTeam, selectedPosition, selectedPlayerType, selectedSeason]);

    const createDropdown = (label, value, setValue, options) => (
        <FormControl sx={{ minWidth: 150, marginRight: 2 }}>
            <InputLabel>{label}</InputLabel>
            <Select
                value={value}
                label={label}
                onChange={(e) => setValue(e.target.value)}
            >
                <MenuItem value="">All</MenuItem>
                {options.map(opt => (
                    <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
            </Select>
        </FormControl>
    );

    const downloadCSV = async () => {
        setDownloading(true);

        try {
            // Calculate total pages needed
            const totalPages = Math.ceil(rowCount / 100); // Use max page size of 100
            const allPlayers = [];

            // Fetch all pages
            for (let page = 1; page <= totalPages; page++) {
                const params = new URLSearchParams();
                params.append('page', page.toString());
                params.append('page_size', '100');

                // Apply current filters
                if (searchTerm) params.append('search', searchTerm);
                if (selectedTeam) params.append('team', selectedTeam);
                if (selectedPosition) params.append('position', selectedPosition);
                if (selectedPlayerType) params.append('player_type', selectedPlayerType);
                if (selectedSeason) params.append('season', selectedSeason);

                const response = await fetch(`${apiUrl}?${params.toString()}`);
                if (!response.ok) {
                    throw new Error(`HTTP error! status: ${response.status}`);
                }

                const responseData = await response.json();
                const data = responseData.data || responseData;

                allPlayers.push(...data.map((player, index) => ({
                    ...player,
                    usernameDisplay: player.user ? player.user.username : 'N/A',
                    user_url: player.user?.url || null,
                    team_name: player.team ? player.team.team_name : 'N/A',
                })));
            }

            // Generate CSV
            const headers = [
                'Player Name', 'Username', 'Number', 'Position', 'Team', 'Player Type',
                'College', 'Throws', 'Bats', 'Recruited By', 'Height', 'Weight', 'Birthplace',
                'Discord Name', 'Hitting Type', 'TPE', 'TPE Banked', 'Season',
                'Last Updated', 'Free Rookie Change', 'Eligible For Callup',
                'Archetype Changes', 'Position Changes'
            ];

            const csvData = allPlayers.map(player => {
                return [
                    player.player_name,
                    player.usernameDisplay,
                    player.number,
                    player.position,
                    player.team_name,
                    player.player_type === 'batter' ? 'Batter' : player.player_type === 'pitcher' ? 'Pitcher' : 'Unknown',
                    player.college,
                    player.throws,
                    player.bats,
                    player.recruited_by,
                    player.height,
                    player.weight,
                    player.birthplace,
                    player.discord_name,
                    player.hitting_type,
                    player.tpe,
                    player.tpe_banked,
                    player.season,
                    player.last_updated ? new Date(player.last_updated).toLocaleDateString() : '',
                    player.free_rookie_change,
                    player.eligible_for_new_create_callup_change,
                    player.total_archetype_changes,
                    player.total_position_changes
                ].map(cell => {
                    const cellStr = cell?.toString() || '';
                    if (cellStr.includes(',') || cellStr.includes('"') || cellStr.includes('\n')) {
                        return `"${cellStr.replace(/"/g, '""')}"`;
                    }
                    return cellStr;
                }).join(',');
            });

            const csvContent = [headers.join(','), ...csvData].join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', `all_player_attributes_${new Date().toISOString().split('T')[0]}.csv`);
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading CSV:', error);
            alert('Error downloading CSV. Please try again.');
        } finally {
            setDownloading(false);
        }
    };

    const columns = useMemo(() => [
        {
            field: 'player_name',
            headerName: 'Player Name',
            width: 180,
            renderCell: (params) => (
                params.row.url ? (
                    <MuiLink
                        href={params.row.url}
                        target="_blank"
                        rel="noopener"
                        sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        {params.value}
                    </MuiLink>
                ) : params.value
            ),
        },
        {
            field: 'usernameDisplay',
            headerName: 'Username',
            width: 130,
            renderCell: (params) => (
                params.row.user_url ? (
                    <MuiLink
                        href={params.row.user_url}
                        target="_blank"
                        rel="noopener"
                        sx={{
                            color: 'primary.main',
                            textDecoration: 'none',
                            '&:hover': {
                                textDecoration: 'underline',
                            },
                        }}
                    >
                        {params.value}
                    </MuiLink>
                ) : params.value
            ),
        },
        {
            field: 'number',
            headerName: 'Number',
            width: 80,
            type: 'number',
        },
        {
            field: 'position',
            headerName: 'Position',
            width: 90,
        },
        {
            field: 'team_name',
            headerName: 'Team',
            width: 180,
        },
        {
            field: 'player_type',
            headerName: 'Player Type',
            width: 110,
            valueFormatter: (value) => {
                if (value === 'batter') return 'Batter';
                if (value === 'pitcher') return 'Pitcher';
                return 'Unknown';
            }
        },
        {
            field: 'college',
            headerName: 'College',
            width: 150,
        },
        {
            field: 'throws',
            headerName: 'Throws',
            width: 90,
        },
        {
            field: 'bats',
            headerName: 'Bats',
            width: 90,
        },
        {
            field: 'recruited_by',
            headerName: 'Recruited By',
            width: 130,
        },
        {
            field: 'height',
            headerName: 'Height',
            width: 90,
        },
        {
            field: 'weight',
            headerName: 'Weight',
            width: 90,
        },
        {
            field: 'birthplace',
            headerName: 'Birthplace',
            width: 150,
        },
        {
            field: 'discord_name',
            headerName: 'Discord Name',
            width: 140,
        },
        {
            field: 'hitting_type',
            headerName: 'Hitting Type',
            width: 120,
        },
        {
            field: 'tpe',
            headerName: 'TPE',
            width: 90,
            type: 'number',
        },
        {
            field: 'tpe_banked',
            headerName: 'TPE Banked',
            width: 110,
            type: 'number',
        },
        {
            field: 'season',
            headerName: 'Season',
            width: 90,
            type: 'number',
        },
        {
            field: 'last_updated',
            headerName: 'Last Updated',
            width: 160,
            valueFormatter: (value) => {
                if (!value) return 'N/A';
                return new Date(value).toLocaleDateString();
            }
        },
        {
            field: 'free_rookie_change',
            headerName: 'Free Rookie Change',
            width: 150,
            type: 'boolean',
        },
        {
            field: 'eligible_for_new_create_callup_change',
            headerName: 'Eligible For Callup',
            width: 160,
            type: 'boolean',
        },
        {
            field: 'total_archetype_changes',
            headerName: 'Archetype Changes',
            width: 150,
            type: 'number',
        },
        {
            field: 'total_position_changes',
            headerName: 'Position Changes',
            width: 150,
            type: 'number',
        },
    ], []);

    if (error) return <div>Error loading data: {error.message}</div>;

    return (
        <Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, marginBottom: 2, alignItems: 'center' }}>
                <TextField
                    label="Search by Player or Username"
                    variant="outlined"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    sx={{ minWidth: 250 }}
                />
                {createDropdown("Team", selectedTeam, setSelectedTeam, teamOptions)}
                {createDropdown("Position", selectedPosition, setSelectedPosition, positionOptions)}
                {createDropdown("Player Type", selectedPlayerType, setSelectedPlayerType, ['batter', 'pitcher', 'unknown'])}
                {createDropdown("Season", selectedSeason, setSelectedSeason, seasonOptions)}
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={downloading ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
                    onClick={downloadCSV}
                    disabled={downloading}
                    sx={{ height: 56 }}
                >
                    {downloading ? 'Downloading...' : 'Download CSV'}
                </Button>
            </Box>
            <div style={{ height: 600, width: '100%', position: 'relative' }}>
                <DataGrid
                    rows={players}
                    columns={columns}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                    pageSizeOptions={[25, 50, 100]}
                    rowCount={rowCount}
                    paginationMode="server"
                    loading={loading}
                    disableSelectionOnClick
                    slots={{
                        loadingOverlay: CenteredLoadingOverlay,
                    }}
                />
            </div>
        </Box>
    );
};

export default AllAttributesTable;
